import { lazy } from 'react';
import { Routes, Route, Navigate, useLocation, Outlet } from 'react-router';
import { AnimatePresence } from 'framer-motion';
import { useApiContext, useGetBlockedCountry } from 'react-easyrocket';
import { MessagesBlock } from 'src/components/MessagesBlock';
import { Modal } from 'src/components/Modal';
import { SocialSignUpHandler } from 'src/components/SocialSignUpHandler';
import { ProtectedAuthRoute } from 'src/components/ProtectedAuthRoute';
import { SendAction } from 'src/components/SendAction';
import { DepositReturn } from 'src/modules/depositReturnPath/components/DepositReturn';
import { useFullScreen } from 'src/hooks/useFullScreen';
import { LANG_PARAM, useRoute } from 'src/router/hooks';
import Layout from 'src/components/Layouts/Layout';

const AccountLayout = lazy(() => import('src/components/Layouts/AccountLayout'));
const GameLayout = lazy(() => import('src/components/Layouts/GameLayout'));

import MainPage from 'src/pages/Main';
const AccountProfile = lazy(() => import('src/pages/AccountProfile'));
const AccountPromo = lazy(() => import('src/pages/AccountPromo'));
const AccountDeposit = lazy(() => import('src/pages/AccountDeposit'));
const AccountJackpots = lazy(() => import('src/pages/AccountJackpots'));
const AccountTournaments = lazy(() => import('src/pages/AccountTounaments'));
const AccountVip = lazy(() => import('src/pages/AccountVip'));
const AccountWallet = lazy(() => import('src/pages/AccountWallet'));
const AccountWithdrawal = lazy(() => import('src/pages/AccountWithdrawal'));
const BlockedCountryPlug = lazy(() => import('src/pages/BlockedCountryPlug'));
const Bonus = lazy(() => import('src/pages/Bonus'));
const Bonuses = lazy(() => import('src/pages/Bonuses'));
// const CatchGame = lazy(() => import('src/pages/CatchGame'));
const ContentPage = lazy(() => import('src/pages/ContentPage'));
const Faqs = lazy(() => import('src/pages/Faqs'));
import GamePage from 'src/pages/Game';
import { isShowEventShop } from 'src/config/helpers';
import { Registration } from 'src/pages/Registration';
import { SideEffects } from './components/SideEffects';
import { LuckyPigeonOpener } from 'src/pages/LuckyPigeonOpener';
import { ActivateBonus } from 'src/pages/ActivateBonus';
import { DepositLink } from 'src/components/DepositLink';
const Games = lazy(() => import('src/pages/Games'));
const Jackpots = lazy(() => import('src/pages/Jackpots'));
const Landing = lazy(() => import('src/pages/Landing'));
const LocalTournament = lazy(() => import('src/pages/LocalTournament'));
const Page404 = lazy(() => import('src/pages/Page404'));
const ProviderTournament = lazy(() => import('src/pages/ProviderTournament'));
const PwaPage = lazy(() => import('src/pages/PwaPage'));
const ReferralPage = lazy(() => import('src/pages/ReferralPage'));
const AuthorizedShop = lazy(() =>
  isShowEventShop()
    ? import('src/pages/EventShop/AuthorizedEventShop')
    : import('src/pages/Shop/AuthorizedShop')
);
const UnauthorizedShop = lazy(() =>
  isShowEventShop()
    ? import('src/pages/EventShop/UnauthorizedEventShop')
    : import('src/pages/Shop/UnauthorizedShop')
);
const Tournaments = lazy(() => import('src/pages/Tournaments'));
const VIP = lazy(() => import('src/pages/VIP'));

export function Router() {
  const { isAuthenticated } = useApiContext();
  const { refFullscreen } = useFullScreen();
  const location = useLocation();
  const { isBlockedCountry } = useGetBlockedCountry();
  const { ROUTE } = useRoute();

  if (isBlockedCountry) {
    return <BlockedCountryPlug />;
  }

  return (
    <div ref={refFullscreen} className="bg-bg_main">
      <MessagesBlock />
      <Modal />
      <AnimatePresence initial={false}>
        <Routes location={location} key="location">
          <Route
            path={LANG_PARAM}
            element={
              <>
                <SideEffects />
                <Outlet />
              </>
            }>
            <Route
              path={ROUTE.ACCOUNT.path}
              element={
                <ProtectedAuthRoute isAuthenticated={isAuthenticated}>
                  <AccountLayout />
                </ProtectedAuthRoute>
              }>
              <Route index element={<Navigate to={ROUTE.ACCOUNT_PROFILE_MAIN.get()} />} />
              <Route path={ROUTE.ACCOUNT_PROFILE.path} element={<AccountProfile />} />
              <Route
                path={ROUTE.ACCOUNT_PROFILE_TOURNAMENTS.path}
                element={<AccountTournaments />}
              />
              <Route path={ROUTE.ACCOUNT_JACKPOTS.path} element={<AccountJackpots />} />
              <Route path={ROUTE.ACCOUNT_USER_BALANCE.path} element={<AccountWallet />} />
              <Route path={ROUTE.ACCOUNT_DEPOSIT.path} element={<AccountDeposit />} />
              <Route path={ROUTE.ACCOUNT_WITHDRAWAL.path} element={<AccountWithdrawal />} />
              <Route path={ROUTE.ACCOUNT_BONUS.path} element={<AccountPromo />} />
              <Route path={ROUTE.ACCOUNT_VIP.path} element={<AccountVip />} />
            </Route>

            <Route element={<Layout />}>
              <Route index element={<MainPage />} />
              <Route path={ROUTE.DEPOSIT_LINK.path} element={<DepositLink />} />
              <Route path={ROUTE.ACTIVATE_BONUS.path} element={<ActivateBonus />} />
              <Route path={ROUTE.AUTH.path} element={<SocialSignUpHandler />} />
              <Route path={ROUTE.MAIN_GAMES.path}>
                <Route index path=":categoryId?" element={<Games />} />
              </Route>
              <Route path={ROUTE.TOURNAMENTS.path} element={<Tournaments />} />
              <Route path={ROUTE.NETWORK_TOURNAMENT.path} element={<ProviderTournament />} />
              <Route path={ROUTE.TOURNAMENTS_LOCAL.path} element={<LocalTournament />} />
              <Route path={ROUTE.BONUSES.path} element={<Bonuses />} />
              <Route path={ROUTE.BONUS.path} element={<Bonus />} />
              <Route path={ROUTE.INFO_PAGES.path} element={<ContentPage />} />
              <Route path={ROUTE.JACKPOTS.path} element={<Jackpots />} />
              <Route path={ROUTE.FAQ.path} element={<Faqs />} />
              <Route path={ROUTE.REGISTRATION.path} element={<Registration />} />
              {/* <Route
                path={ROUTE.CATCH_GAME.path}
                element={isEventActive ? <CatchGame /> : <Navigate to={ROUTE.MAIN.get()} />}
              /> */}
              {/* <Route
              path={ROUTE.SHOP.path}
              element={isAuthenticated ? <Shop /> : <ShopUnauthorized />}
            /> */}
              <Route
                path={ROUTE.SHOP.path}
                element={isAuthenticated ? <AuthorizedShop /> : <UnauthorizedShop />}
              />
              {/* <Route path={ROUTE.HALLOWEEN.path} element={<Halloween />} /> */}
              <Route path={ROUTE.VIP.path} element={<VIP />} />
              <Route path={ROUTE.PWA_INSTRUCTION.path} element={<PwaPage />} />
            </Route>
            <Route path={ROUTE.GAME.path} element={<GameLayout />}>
              <Route index element={<GamePage />} />
            </Route>
            <Route path={ROUTE.DEPOSIT_SUCCESS.path} element={<DepositReturn status="success" />} />
            <Route path={ROUTE.DEPOSIT_FAILURE.path} element={<DepositReturn status="failure" />} />
            <Route path={ROUTE.DEPOSIT_CANCEL.path} element={<DepositReturn status="cancel" />} />
            <Route path={ROUTE.DEPOSIT_RETURN.path} element={<DepositReturn status="return" />} />

            <Route
              path={ROUTE.LANDING_WELCOME_BONUS.path}
              element={isAuthenticated ? <Navigate to={'/'} /> : <Landing />}
            />
            <Route path={ROUTE.ACTION.path} element={<SendAction />} />
            <Route path={ROUTE.REGISTER_REFERRAL.path} element={<ReferralPage />} />
            <Route path={ROUTE.PWA.path} element={<Navigate to={ROUTE.MAIN.get()} />} />
            <Route path={ROUTE.REDIRECT_LOBBY.path} element={<Navigate to={ROUTE.MAIN.get()} />} />
            <Route path={ROUTE.LUCKY_PIGEON_OPENER.path} element={<LuckyPigeonOpener />} />
            <Route path={ROUTE.ALL_ROUTES.path} element={<Navigate to={'/404'} />} />
            <Route path={ROUTE[404].path} element={<Page404 />} />
          </Route>
        </Routes>
      </AnimatePresence>
    </div>
  );
}
